import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface AdminPaginationProps {
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  loading?: boolean;
  className?: string;
}

export const AdminPagination: React.FC<AdminPaginationProps> = ({
  pagination,
  onPageChange,
  onLimitChange,
  loading = false,
  className = ""
}) => {
  const { page, limit, total, totalPages } = pagination;

  const canGoToPrevious = page > 1;
  const canGoToNext = page < totalPages;

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const handleFirstPage = () => onPageChange(1);
  const handlePreviousPage = () => onPageChange(Math.max(1, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages, page + 1));
  const handleLastPage = () => onPageChange(totalPages);

  return (
    <div className={`flex items-center justify-between space-x-4 ${className}`}>
      {/* Results Info */}
      <div className="text-sm text-gray-600">
        {total > 0 ? (
          <>Mostrando <span className="font-medium">{startItem}</span> a <span className="font-medium">{endItem}</span> de <span className="font-medium">{total}</span> resultados</>
        ) : (
          'Sem resultados'
        )}
      </div>

      {/* Page Size Selector */}
      <div className="flex items-center space-x-2">
        <span className="text-sm text-gray-600">Mostrar:</span>
        <Select
          value={limit.toString()}
          onValueChange={(value) => onLimitChange(Number(value))}
          disabled={loading}
        >
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="20">20</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleFirstPage}
          disabled={!canGoToPrevious || loading}
          className="h-8 w-8 p-0"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousPage}
          disabled={!canGoToPrevious || loading}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else {
              const start = Math.max(1, page - 2);
              const end = Math.min(totalPages, start + 4);
              pageNum = start + i;
              if (pageNum > end) return null;
            }

            return (
              <Button
                key={pageNum}
                variant={pageNum === page ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                disabled={loading}
                className="h-8 w-8 p-0"
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleNextPage}
          disabled={!canGoToNext || loading}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleLastPage}
          disabled={!canGoToNext || loading}
          className="h-8 w-8 p-0"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Hook for managing pagination state
 */
export const usePagination = (initialLimit = 20) => {
  const [pagination, setPagination] = React.useState<PaginationState>({
    page: 1,
    limit: initialLimit,
    total: 0,
    totalPages: 0
  });

  const updatePagination = React.useCallback((updates: Partial<PaginationState>) => {
    setPagination(prev => {
      const newState = { ...prev, ...updates };
      // Recalculate totalPages when total or limit changes
      if ('total' in updates || 'limit' in updates) {
        newState.totalPages = Math.ceil(newState.total / newState.limit);
      }
      // Ensure page doesn't exceed totalPages
      if (newState.page > newState.totalPages && newState.totalPages > 0) {
        newState.page = newState.totalPages;
      }
      return newState;
    });
  }, []);

  const setPage = React.useCallback((page: number) => {
    updatePagination({ page });
  }, [updatePagination]);

  const setLimit = React.useCallback((limit: number) => {
    updatePagination({ limit, page: 1 }); // Reset to first page when changing limit
  }, [updatePagination]);

  const setTotal = React.useCallback((total: number) => {
    updatePagination({ total });
  }, [updatePagination]);

  const reset = React.useCallback(() => {
    setPagination({
      page: 1,
      limit: initialLimit,
      total: 0,
      totalPages: 0
    });
  }, [initialLimit]);

  return {
    pagination,
    setPage,
    setLimit,
    setTotal,
    updatePagination,
    reset
  };
};